/**
 * Communication webhook-hardening events — catalog v9 additions
 * (commerce-platform Phase 5, plan §5 "Event catalog v9" / §6 Step 5).
 *
 * - `communication:support-ticket-created`  — a support ticket was created
 *   (via the agent `create_support_ticket` tool target API or the owner
 *   CRUD). Auditable so every ticket creation is reconstructable (§9 audit
 *   coverage). Payload is PII-minimized (§9): never the ticket body.
 * - `communication:webhook-secret-rotated`  — a webhook's reversible HMAC
 *   signing secret was rotated/provisioned in the secrets-service vault,
 *   with a grace window for the previous secret.
 *
 * Both are `auditable: true`.
 */
import {
  asEventRecord,
  defineEvent,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface SupportTicketCreatedPayload extends Record<string, unknown> {
  ticketId: string;
  orgId: string;
  subject: string;
  status: string;
  createdBy: string;
  customerId?: string;
  agentId?: string;
}

export interface WebhookSecretRotatedPayload extends Record<string, unknown> {
  webhookId: string;
  vaultId: string;
  rotatedAt: string;
  graceExpiresAt?: string;
}

const SUPPORT_TICKET_CREATED_TYPE = 'communication:support-ticket-created';
const supportTicketCreatedSchema: EventSchema<SupportTicketCreatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, SUPPORT_TICKET_CREATED_TYPE);
    const customerId = optionalString(record.customerId, 'customerId', SUPPORT_TICKET_CREATED_TYPE);
    const agentId = optionalString(record.agentId, 'agentId', SUPPORT_TICKET_CREATED_TYPE);
    return {
      ticketId: requireString(record.ticketId, 'ticketId', SUPPORT_TICKET_CREATED_TYPE),
      orgId: requireString(record.orgId, 'orgId', SUPPORT_TICKET_CREATED_TYPE),
      subject: requireString(record.subject, 'subject', SUPPORT_TICKET_CREATED_TYPE),
      status: requireString(record.status, 'status', SUPPORT_TICKET_CREATED_TYPE),
      createdBy: requireString(record.createdBy, 'createdBy', SUPPORT_TICKET_CREATED_TYPE),
      ...(customerId === undefined ? {} : { customerId }),
      ...(agentId === undefined ? {} : { agentId }),
    };
  },
};

export const SUPPORT_TICKET_CREATED_EVENT = defineEvent<SupportTicketCreatedPayload>({
  type: SUPPORT_TICKET_CREATED_TYPE,
  version: 1,
  schema: supportTicketCreatedSchema,
  auditable: true,
  description: 'A support ticket was created in the communication service (PII-minimized payload).',
  since: '1.11.0',
});

const WEBHOOK_SECRET_ROTATED_TYPE = 'communication:webhook-secret-rotated';
const webhookSecretRotatedSchema: EventSchema<WebhookSecretRotatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, WEBHOOK_SECRET_ROTATED_TYPE);
    const graceExpiresAt = optionalString(record.graceExpiresAt, 'graceExpiresAt', WEBHOOK_SECRET_ROTATED_TYPE);
    return {
      webhookId: requireString(record.webhookId, 'webhookId', WEBHOOK_SECRET_ROTATED_TYPE),
      vaultId: requireString(record.vaultId, 'vaultId', WEBHOOK_SECRET_ROTATED_TYPE),
      rotatedAt: requireString(record.rotatedAt, 'rotatedAt', WEBHOOK_SECRET_ROTATED_TYPE),
      ...(graceExpiresAt === undefined ? {} : { graceExpiresAt }),
    };
  },
};

export const WEBHOOK_SECRET_ROTATED_EVENT = defineEvent<WebhookSecretRotatedPayload>({
  type: WEBHOOK_SECRET_ROTATED_TYPE,
  version: 1,
  schema: webhookSecretRotatedSchema,
  auditable: true,
  description: 'A webhook HMAC signing secret was rotated with a grace window (secret material never included).',
  since: '1.11.0',
});

export const COMMUNICATION_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [SUPPORT_TICKET_CREATED_EVENT.type]: SUPPORT_TICKET_CREATED_EVENT,
  [WEBHOOK_SECRET_ROTATED_EVENT.type]: WEBHOOK_SECRET_ROTATED_EVENT,
};
